import { FormikProvider, useFormik } from "formik";
import React, { useState } from "react";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import SharedComponents from "@audacia-hq/shared/components";

import CountrySelect from "../form/CountrySelect";
import { canadianRegion } from "../../services/countryUtils";
import UsageConsent from "../member-settings/consents/UsageConsent";
import Button from "../Button";
import { AppUserType } from "../../context/AuthContext";
import { useAppContext } from "../../context/AppContext";
import ConsentSwitch from "../member-settings/consents/ConsentSwitch";
import { useLocale } from "../../context/LocaleContext";

export interface CreateMemberProfileForm {
  country?: string;
  email: string;
  lang: string;
  region?: string;
  usageConsent: boolean;
  newsletterConsent: boolean;
  exclusiveOffersConsent: boolean;
}

interface Props {
  user: AppUserType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onCreateMember: (form: CreateMemberProfileForm) => Promise<any>;
}

const CreateMemberProfile: React.FC<Props> = ({ user, onCreateMember }) => {
  const { ipCountry } = useAppContext();
  const { t } = useTranslation("member");
  const locale = useLocale();
  const [showUsage, setShowUsage] = useState(false);
  const [loading, setLoading] = useState(false);

  const validationSchema = Yup.object().shape({
    email: Yup.string().email("email.invalid").required("required"),
    country: Yup.string().length(2).required("required"),
    region: Yup.string()
      .length(2)
      .when("country", {
        is: "CA",
        then: () => Yup.string().required("required").length(2),
      }),
    usageConsent: Yup.boolean().isTrue("usageConsentTrue").required("required"),
    lang: Yup.string().length(2).required("required"),
  });

  const formik = useFormik<CreateMemberProfileForm>({
    initialValues: {
      email: user?.email || "",
      usageConsent: false,
      newsletterConsent: false,
      exclusiveOffersConsent: false,
      lang: locale.toUpperCase(),
      country: ipCountry || "",
    },
    validateOnChange: false,
    validateOnBlur: false,
    validationSchema,
    onSubmit: (values) => {
      setLoading(true);
      onCreateMember(values).finally(() => setLoading(false));
    },
  });

  return (
    <>
      {showUsage ? (
        <UsageConsent onBack={() => setShowUsage(false)} />
      ) : (
        <FormikProvider value={formik}>
          {/* If no initial email, usually means it's from a FB account with no email */}
          {!formik.initialValues.email && (
            <div className="my-3">
              <label htmlFor="email">
                {t("form.email")}
                {" "}
                <span className="text-red-500">*</span>
                <SharedComponents.Form.Input
                  value={formik.values.email}
                  onChange={(e) => formik.setFieldValue("email", e.target.value)}
                  error={formik.errors.email}
                />
                {formik.errors.email && (
                  <span className="text-red-500">
                    {t(`common:errors.${formik.errors.email}`)}
                  </span>
                )}
              </label>
            </div>
          )}
          <div className="my-3" data-pw="memberProfile">
            <label htmlFor="country">
              {t("form.country")}
              {" "}
              <span className="text-red-500">*</span>
              <CountrySelect
                selected={formik.values.country}
                error={formik.errors.country !== undefined}
                setSelected={(value) => formik.setFieldValue("country", value)}
                dataPW="memberCountry"
              />
              {formik.errors.country && (
                <span className="text-red-500">
                  {t(`common:errors.${formik.errors.country}`)}
                </span>
              )}
            </label>
          </div>
          {formik.values.country === "CA" && (
            <div className="my-3">
              <label htmlFor="lang">
                {t("form.region")}
                {" "}
                <span className="text-red-500">*</span>
                <SharedComponents.Form.Select
                  value={formik.values.region}
                  onChange={(value) => formik.setFieldValue("region", value)}
                  renderSelected={(value) => (canadianRegion.find((r) => r[0] === value) || ["", ""])[1]}
                  error={formik.errors.region !== undefined}
                >
                  {canadianRegion.map((r) => (
                    <SharedComponents.Form.Option key={r[0]} value={r[0]} text={r[1]} />
                  ))}
                </SharedComponents.Form.Select>
                {formik.errors.region && (
                  <span className="text-red-500">
                    {t(`common:errors.${formik.errors.region}`)}
                  </span>
                )}
              </label>
            </div>
          )}
          <div className="flex flex-col gap-3 my-12">
            <div>
              <ConsentSwitch consentKey="subscription.newsletter" value={formik.values.newsletterConsent} disabled={false} onChange={(v) => formik.setFieldValue("newsletterConsent", v)} />
            </div>
            <div>
              <ConsentSwitch consentKey="subscription.exclusiveOffers" value={formik.values.exclusiveOffersConsent} disabled={false} onChange={(v) => formik.setFieldValue("exclusiveOffersConsent", v)} />
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                name="usageConsent"
                id="usageConsent"
                checked={formik.values.usageConsent}
                onChange={(e) => formik.setFieldValue("usageConsent", e.target.checked)}
                className="checkbox"
                data-pw="usageConsent"
              />
              <label htmlFor="usageConsent">
                {t("registerConsent")}
                {" "}
                <button type="button" onClick={() => setShowUsage(true)} className="text-blue-600 underline hover:text-blue-700">{t("registerConditions")}</button>
                {" "}
                <span className="text-red-500">*</span>
              </label>
            </div>
            {formik.errors.usageConsent && (
              <div className="text-red-500">
                {t(`common:errors.${formik.errors.usageConsent}`)}
              </div>
            )}
          </div>
          <div className="w-full mt-12">
            <Button className="w-full" onClick={() => formik.handleSubmit()} loading={loading} data-pw="memberProfileSubmit">{t("common:accept")}</Button>
          </div>
        </FormikProvider>
      )}
    </>
  );
};

export default CreateMemberProfile;
